import { useState, useEffect } from "react";
import { observer, inject } from "mobx-react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { useInput, inputsHasError } from "../utils/hooks";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isNotEmpty, withError, isValidEmailAddress, isStrongPassword } from "../validation";
import CoralisLogo from "../asserts/img/CoralisLogo.svg";
import loginImg from "../asserts/img/coralis_login_image.jpg";
import emailImg from "../asserts/img/@.svg";
import pwdImg from "../asserts/img/password.svg";
import showPasswordImg from "../asserts/img/ic_eye.svg";
import hidePasswordImg from "../asserts/img/eye.svg";
import { Button, Text } from "../UI";
import { ClipLoader } from "react-spinners";
import { InputMUI } from "../UI/Input";
import ChangeLanguage from "../components/ChangeLanguage";
import { useMobile } from "../utils/mobile";

const ScreenType = {
  LOGIN: "login",
  NEW_PASSWORD: "new_password",
  FORGOT_PASSWORD: "forgot_password",
};

const StyledDiv = styled.div`
  margin-bottom: 1em;
  margin-top: 1em;
`;

const Login = ({ userStore, type }) => {
  const [searchParams] = useSearchParams();
  const decryptUser = searchParams.get("u");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMobile();
  const { t, i18n } = useTranslation("login");
  const isRtl = i18n.dir() === "rtl";
  const redirectAfterLogin = window.sessionStorage.getItem("redirectAfterLogin");
  const redirectAfterLoginParams = window.sessionStorage.getItem("redirectAfterLoginParams");

  const isNotEmptyWithError = withError(isNotEmpty, t("error:text_field_empty_error"));
  const isValidEmailAddressWithError = withError(
    isValidEmailAddress,
    t("text_invalid_email_address_error")
  );
  const isNotStrongPasswordWithError = withError(
    isStrongPassword,
    t("text_field_not_strong_password_error")
  );

  const isNotEqual = (input) => {
    const inputString = String(input);
    return inputString === currentUser.pwd;
  };

  const isNotEqualWithError = withError(isNotEqual, t("text_not_equal_error"));

  function useStickyState(defaultValue, key, prop) {
    const [value, setValue] = useState(() => {
      const stickyValue = window.localStorage.getItem(key);
      return stickyValue !== null ? JSON.parse(stickyValue)[prop] : defaultValue;
    });

    useEffect(() => {
      const stickyData = window.localStorage.getItem(key);
      const data = stickyData !== null ? JSON.parse(stickyData) : {};
      data[prop] = value;
      window.localStorage.setItem(key, JSON.stringify(data));
    }, [key, value, prop]);

    return [value, setValue];
  }

  const [currentUser, setUser] = useStickyState(
    {
      email: userStore?.userDetails?.email || "",
      pwd: userStore?.userDetails?.pwd || "",
    },
    "coralis_office-user",
    "userDetails"
  );

  // Clear sticky state when user is not authenticated (after logout)
  useEffect(() => {
    if (!userStore.isAuthenticated) {
      setUser({ email: "", pwd: "" });
    }
  }, [userStore.isAuthenticated, setUser]);

  const getTitle = (type) => {
    switch (type) {
      case ScreenType.LOGIN:
        return t("login");
      case ScreenType.NEW_PASSWORD:
        return t("new_password");
      case ScreenType.FORGOT_PASSWORD:
        return t("forgot_password");
      default:
        return "";
    }
  };

  const getSubTitle = (type) => {
    switch (type) {
      case ScreenType.LOGIN:
        return t("login_text");
      case ScreenType.NEW_PASSWORD:
        return t("new_password_text");
      case ScreenType.FORGOT_PASSWORD:
        return t("forgot_password_text");
      default:
        return "";
    }
  };

  const handleChange = (prop) => (event) => {
    setUser({ ...currentUser, [prop]: event.target.value });
  };

  const email = useInput({
    validation: [isNotEmptyWithError, isValidEmailAddressWithError],
    placeholder: t("email_placeholder"),
    value: currentUser.email || "",
    helperText:
      userStore.error === "error:error_failed_login_user_not_exists"
        ? "error:error_failed_login_user_not_exists"
        : "",
  });

  const pwd = useInput({
    placeholder: type === ScreenType.LOGIN ? t("pwd_placeholder") : t("new_pwd_placeholder"),
    validation:
      type === ScreenType.NEW_PASSWORD
        ? [isNotEmptyWithError, isNotStrongPasswordWithError]
        : [isNotEmptyWithError],
    value: currentUser.pwd || "",
    helperText:
      userStore.error === "error:error_failed_login_wrong_cardential"
        ? "error:error_failed_login_wrong_cardential"
        : "",
  });

  const newPwd = useInput({
    placeholder: t("new_pwd_again_placeholder"),
    validation: [isNotEmptyWithError, isNotEqualWithError, isNotStrongPasswordWithError],
    value: currentUser.newPwd || "",
  });

  const handleSubmit = async () => {
    if (isLoading) return;

    if (!inputsHasError(email, pwd)) {
      const data = {
        USERNAME: currentUser.email,
        WEBPASSWORD: currentUser.pwd,
      };
      setLoading(true);
      const res = await userStore.login(data);
      setLoading(false);
      if (res.isSucceed) {
        if (userStore.userDetails.ZCR_WEBPASSCHNGFLAG === "Y") {
          const encrypted = await userStore.getEncryptedUser(data);
          if (encrypted) {
            navigate(`/new_password?u=${encrypted}`);
          } else {
            return;
          }
        } else {
          if (!!redirectAfterLogin) {
            navigate(redirectAfterLogin + redirectAfterLoginParams);
          } else {
            navigate("/support_calls");
          }
        }
      }
    }
  };

  const handleSend = async () => {
    if (isLoading) return;

    if (!inputsHasError(email)) {
      const data = {
        USERNAME: currentUser.email,
        body_subject: t("pwd_subject"),
        href: `${window.location.origin}/new_password`,
      };
      setLoading(true);
      const userResponse = await userStore.sendResetPassword(data);
      setLoading(false);

      if (userResponse.error) {
        setErrorMessage(userResponse.error);
      } else {
        navigate("/login");
      }
    }
  };

  const handleResetPassword = async () => {
    if (isLoading) return;

    if (!inputsHasError(pwd, newPwd)) {
      const data = {
        DECRYPT_USERNAME: decryptUser,
        WEBPASSWORD: currentUser.newPwd,
        ZCR_WEBPASSCHNGFLAG: "N",
      };
      setLoading(true);
      const res = await userStore.resetPassword(data);
      setLoading(false);

      if (res.isSucceed) {
        navigate("/login");
      }
    }
  };

  const handelByType = async (e, type) => {
    switch (type) {
      case ScreenType.LOGIN: {
        handleSubmit();
        break;
      }
      case ScreenType.FORGOT_PASSWORD:
        handleSend();
        break;
      case ScreenType.NEW_PASSWORD:
        handleResetPassword();
        break;
      default:
        return "";
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  useEffect(() => {
    if (
      userStore.error &&
      userStore.error !== "error:error_failed_login_wrong_cardential" &&
      userStore.error !== "error:error_failed_login_user_not_exists"
    ) {
      setErrorMessage(t(userStore.error));

      // Clear error message after 5 seconds
      const timer = setTimeout(() => setErrorMessage(""), 5000);

      return () => clearTimeout(timer); // Cleanup timeout on unmount
    }
  }, [userStore.error, t]);

  return (
    <>
      {console.log("for_debug")}
      <div
        style={{
          backgroundImage: `url(${loginImg})`,
          backgroundcolor: theme.color.blue,
          backgroundRepeat: isMobile ? `repeat` : `no-repeat`,
          backgroundSize: `100%`,
          height: isMobile ? "50vh" : "100vh",
          width: "100vw",
          backgroundColor: "#3B67AD",
          backgroundBlendMode: "soft-light  ",
        }}
      >
        <div
          style={{
            backgroundColor: isMobile ? `transparent` : theme.color.blue,
            width: isMobile ? "100%" : "25%",
            height: "100vh",
          }}
        >
          <div
            style={{
              position: "relative",
              backgroundColor: theme.color.background,
              top: "15%",
              right: !isMobile && isRtl && "50%",
              left: !isMobile && !isRtl && "50%",
              borderRadius: 36,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                padding: 25,
                gap: 14,
              }}
            >
              <img
                src={CoralisLogo}
                alt="CoralisLogo"
                style={{ width: isMobile && 180, margin: "30px auto 0" }}
              />
              <Text
                style={{
                  color: `${theme.color.darkGray}`,
                  fontWeight: 900,
                  fontSize: 27.34,
                  margin: "0.2em",
                  marginBottom: "0",
                }}
              >
                {getTitle(type)}
              </Text>
              <Text
                style={{
                  color: "#9FA2B4",
                  fontWeight: 700,
                  fontSize: 14,
                  margin: "0.2em",
                  marginTop: "0",
                }}
              >
                {getSubTitle(type)}
              </Text>
              {(type === ScreenType.LOGIN || type === ScreenType.FORGOT_PASSWORD) && (
                <StyledDiv>
                  <InputMUI
                    {...email}
                    onKeyPress={handleKeyPress}
                    onChange={handleChange("email")}
                    startImg={emailImg}
                    isRtl={isRtl}
                  ></InputMUI>
                </StyledDiv>
              )}
              {(type === ScreenType.LOGIN || type === ScreenType.NEW_PASSWORD) && (
                <StyledDiv>
                  <InputMUI
                    {...pwd}
                    onChange={handleChange("pwd")}
                    type={showNewPassword ? "text" : "password"}
                    startImg={pwdImg}
                    onKeyPress={handleKeyPress}
                    showPassword={setShowNewPassword}
                    endImg={showNewPassword ? hidePasswordImg : showPasswordImg}
                    isRtl={isRtl}
                  ></InputMUI>
                </StyledDiv>
              )}
              {type === ScreenType.NEW_PASSWORD && (
                <StyledDiv>
                  <InputMUI
                    placeholder={t("newPwd")}
                    startImg={pwdImg}
                    endImg={showNewPassword ? hidePasswordImg : showPasswordImg}
                    {...newPwd}
                    onBlur={handleChange("newPwd")}
                    showPassword={setShowNewPassword}
                    type={showNewPassword ? "text" : "password"}
                    style={{ textAlign: "center" }}
                    isRtl={isRtl}
                  />
                </StyledDiv>
              )}
              {type === ScreenType.LOGIN && (
                <Text
                  onClick={() => navigate("/forgot_password")}
                  style={{
                    color: "#0063FF",
                    fontWeight: 400,
                    fontSize: 14,
                    margin: "0.2em",
                    alignSelf: "start",
                    cursor: "pointer",
                  }}
                >
                  {t("forgot_password_q")}
                </Text>
              )}
              {type === ScreenType.FORGOT_PASSWORD && (
                <Text
                  onClick={() => navigate("/login")}
                  style={{
                    color: "#0063FF",
                    fontWeight: 400,
                    fontSize: 14,
                    margin: "0.2em",
                    alignSelf: "start",
                    cursor: "pointer",
                  }}
                >
                  {t("back_to_login")}
                </Text>
              )}
              <Button onClick={(e) => handelByType(e, type)} style={{ alignSelf: "center" }}>
                {isLoading ? (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <ClipLoader color={theme.color.white} size={24} />
                  </div>
                ) : (
                  t("send")
                )}
              </Button>
              {errorMessage && <Text.Error>{errorMessage}</Text.Error>}
              <ChangeLanguage inLogin={true} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default inject("userStore")(observer(Login));
export { ScreenType };
